import { Injectable, NgZone } from '@angular/core';
import { RecordedAction, GestureMode } from '../Models/recorded-action-model';
import { MobileRestService } from './mobile-rest.service';
import { MobileDevice } from '../Views/apk-upload/apk-upload.component';
import { Literals } from '../literals';

export type AppType = 'native' | 'flutter';

// ── Flutter Integration page-source endpoint ──────────────────────────────────
const FLUTTER_SOURCE_URL = (baseUrl: string, sessionId: string) =>
  `${baseUrl}/wd/hub/session/${sessionId}/appium/app/flutter_page_source`;

@Injectable({ providedIn: 'root' })
export class MobileSessionService {

  sessionId: string | null  = null;
  udid: string | null        = null;
  appPackage: string | null  = null;
  imageWidth                 = 1080;
  imageHeight                = 2280;
  deviceResolutionResolved   = false;
  isEmulator                 = false;

  /** Set before calling createSession() */
  appType: AppType = 'native';

  /**
   * The device selected by the user in the upload panel.
   * Drives: upload URL, Appium base URL, session capabilities (udid).
   */
  selectedDevice: MobileDevice | null = null;

  /**
   * Resolved after createSession().
   * 'flutter-integration' means the flutter-integration Appium driver is active.
   * 'uiautomator2' is the native Android driver.
   */
  activeDriver: 'uiautomator2' | 'flutter-integration' = 'uiautomator2';

  // ── Session persistence key ───────────────────────────────────────────────────
  private readonly SESSION_KEY = 'ze_mobile_session';

  constructor(
    private ngZone: NgZone,
    private rest: MobileRestService
  ) {}

  private mCall<T = any>(
    apiKey: Parameters<MobileRestService['call']>[1],
    extraParams: Record<string, string | number> = {},
    body?: unknown
  ): Promise<import('./mobile-rest.service').ApiResponse<T>> {
    return this.rest.call<T>(
      this.appiumBaseUrl,
      apiKey,
      { sessionId: this.sessionId || '', ...extraParams },
      body
    );
  }

  // ── Persist / restore session across page refreshes ───────────────────────────

  saveSessionToStorage(): void {
    if (!this.sessionId) return;
    try {
      const snapshot = {
        sessionId:    this.sessionId,
        udid:         this.udid,
        appPackage:   this.appPackage,
        imageWidth:   this.imageWidth,
        imageHeight:  this.imageHeight,
        isEmulator:   this.isEmulator,
        activeDriver: this.activeDriver,
        appType:      this.appType,
        selectedDevice: this.selectedDevice,
      };
      sessionStorage.setItem(this.SESSION_KEY, JSON.stringify(snapshot));
    } catch {}
  }

  restoreSessionFromStorage(): boolean {
    try {
      const raw = sessionStorage.getItem(this.SESSION_KEY);
      if (!raw) return false;
      const s = JSON.parse(raw);
      if (!s.sessionId) return false;
      this.sessionId                = s.sessionId;
      this.udid                     = s.udid;
      this.appPackage               = s.appPackage;
      this.imageWidth               = s.imageWidth  || 1080;
      this.imageHeight              = s.imageHeight || 2280;
      this.isEmulator               = s.isEmulator  || false;
      this.activeDriver             = s.activeDriver || 'uiautomator2';
      this.appType                  = s.appType      || 'native';
      this.selectedDevice           = s.selectedDevice || null;
      this.deviceResolutionResolved = true;
      return true;
    } catch { return false; }
  }

  clearSessionFromStorage(): void {
    try { sessionStorage.removeItem(this.SESSION_KEY); } catch {}
  }

  // ── Dynamic base URL from selected device ─────────────────────────────────────

  get appiumBaseUrl(): string {
    if (this.selectedDevice.domain) {
      return `https://${this.selectedDevice.domain.toLowerCase()}.xpeer.ai`;
    }
    // Fallback to default lab if no device is set
    return 'https://mobilelab4.xpeer.ai';
  }

  get appiumWdUrl(): string {
    return `${this.appiumBaseUrl}/wd/hub`;
  }

  get deviceFarmBaseUrl(): string {
    return `${this.appiumBaseUrl}/device-farm`;
  }

  // ── Session ──────────────────────────────────────────────────────────────────

  async createSession(uploadedAppName: string): Promise<void> {
    const appUrl = `${this.deviceFarmBaseUrl}/apps/${uploadedAppName}`;
    // console.log(`Session appUrl: ${appUrl} | appType: ${this.appType}`);
    // console.log(`Using Appium base: ${this.appiumBaseUrl}`);
    if (this.selectedDevice) {
      // console.log(`📱 Target device: ${this.selectedDevice.device_name} | udid: ${this.selectedDevice.udid}`);
      console.log(`session started`);
    }

    // ── Build capabilities based on selected app type ─────────────────────────
    const alwaysMatch: Record<string, unknown> =
      this.appType === 'flutter'
        ? {
            platformName:                            'android',
            'appium:automationName':                 'flutter',
            'appium:newCommandTimeout':               700000,
            'appium:autoGrantPermissions':            true,
            'appium:skipDeviceInitialization':        true,
            'appium:adbExecTimeout':                  60000,
            'appium:uiautomator2ServerLaunchTimeout': 60000,
            'appium:retryBackoffTime':                500,
            'appium:maxRetryCount':                   1,
            // Pin to the selected device's udid so Appium doesn't pick a random device
            ...(this.selectedDevice ? { 'appium:udid': this.selectedDevice.udid } : {}),
          }
        : {
            platformName:                            'android',
            'appium:automationName':                 'UiAutomator2',
            'appium:newCommandTimeout':               700000,
            'appium:autoGrantPermissions':            true,
            'appium:skipDeviceInitialization':        true,
            'appium:adbExecTimeout':                  60000,
            'appium:uiautomator2ServerLaunchTimeout': 60000,
            'appium:enableMultiWindows': true,
            // Pin to the selected device's udid so Appium doesn't pick a random device
            ...(this.selectedDevice ? { 'appium:udid': this.selectedDevice.udid } : {}),
          };

    // POST to the domain-specific Appium hub

    const res = await this.rest.call<any>(
      this.appiumBaseUrl,
      'createSession',
      {},
      { capabilities: { firstMatch: [{}], alwaysMatch } }
    );

    if (!res.ok || !res.data) {
      throw new Error(`Session failed: HTTP ${res.status} — ${res.error}`);
    }

    this.sessionId = res.data.value.sessionId || res.data.sessionId;
    if (!this.sessionId) throw new Error('sessionId missing');

    const caps = res.data.value.capabilities || {};
    this.udid       = caps.udid || caps.deviceUDID || caps.deviceName || this.selectedDevice.udid || null;
    this.appPackage = caps.appPackage || caps['appium:appPackage'] || null;

    // ── Confirm which driver is actually active ────────────────────────────────
    const automationName: string = (
      caps.automationName || caps['appium:automationName'] || ''
    ).toLowerCase();

    if (this.appType === 'flutter' && automationName.includes('flutter')) {
      this.activeDriver = 'flutter-integration';
      // console.log('✅ Flutter Integration driver confirmed by server caps.');
    } else {
      this.activeDriver = 'uiautomator2';
      if (this.appType === 'flutter') {
        console.warn(
          'Flutter app selected but server returned automationName=' +
          automationName + ' — falling back to UiAutomator2 parser.'
        );
      }
    }

    // if (this.appPackage) console.log(`App package: ${this.appPackage}`);
    if (this.appPackage) console.log('');

    // Resolution strategy 1: deviceScreenSize cap
    const screenSize: string = caps.deviceScreenSize || '';
    if (screenSize) {
      const [w, h] = screenSize.split('x').map(Number);
      if (w > 0 && h > 0) {
        this.imageWidth = w; this.imageHeight = h;
        this.deviceResolutionResolved = true;
        console.log(`${w}x${h}`);
      }
    }

    // Resolution strategy 2: viewportRect
    if (!this.deviceResolutionResolved && caps.viewportRect) {
      const w = caps.viewportRect.width;
      const h = caps.viewportRect.height + (caps.statBarHeight || 0);
      if (w > 0 && h > 0) {
        this.imageWidth = w; this.imageHeight = h;
        this.deviceResolutionResolved = true;
        console.log(`${w}x${h}`);
      }
    }

    // Resolution strategy 3: API calls
    if (!this.deviceResolutionResolved) {
      await this.fetchDeviceResolution();
    }

    // Detect emulator (also use the device registry flag if available)
    this.isEmulator = this.selectedDevice.real_device === false || !!(
      caps.isEmulator
      || (caps.deviceModel && /emulator|sdk_gphone|generic/i.test(caps.deviceModel))
      || (caps.deviceName  && /emulator|generic/i.test(caps.deviceName))
      || (caps.udid        && /emulator/i.test(caps.udid))
    );

    // ── Steps 2-5: Install, detect package/activity, launch ───────────────────
    // Wrapped in try/catch so session is always cleaned up on any failure.
    // const execUrl = `${this.appiumWdUrl}/session/${this.sessionId}/execute/sync`;
    try {

      // ── Step 2: Install APK ─────────────────────────────────────────────────
      const isXapk = uploadedAppName.toLowerCase().endsWith('.xapk');
        if (isXapk) {
          await this.installXapk(appUrl);
        } else {
          const installRes = await this.mCall<any>('installApp', {}, {
            appPath: appUrl,
            options: { replace: true, allowTestPackages: true, useSdkAdb: true }
          });
          if (!installRes.ok) throw new Error(`APK install failed: ${installRes.error}`);
        }

      // ── Step 3: Get package name via pm list packages -3 ───────────────────

      const pmRes = await this.mCall<any>('executeSync', {}, {
        script: 'mobile: shell',
        args:   [{ command: 'pm', args: ['list', 'packages', '-3'] }]
      });
      if (!pmRes.ok) throw new Error(`pm list packages failed: ${pmRes.error}`);

      const EXCLUDED_PACKAGES = [
        'com.device.farm',
        'androidx.test.orchestrator',
        'androidx.test.services',
        'io.appium.uiautomator2.server',
        'io.appium.settings',
        'io.appium',
      ];
      const packages = (pmRes.data.value || '')
        .split('\n')
        .map((l: string) => l.replace(/^package:/, '').trim())
        .filter(Boolean)
        .filter((p: string) => !EXCLUDED_PACKAGES.some(ex => p.startsWith(ex)));

      if (packages.length === 0) throw new Error('No third-party packages found after install');
      this.appPackage = packages[packages.length - 1];
      console.log('Detected package:', this.appPackage);

      // ── Step 4: Get launcher activity via dumpsys ───────────────────────────

      const dumpsysRes = await this.mCall<any>('executeSync', {}, {
        script: 'mobile: shell',
        args:   [{ command: 'dumpsys', args: ['package', this.appPackage] }]
      });
      if (!dumpsysRes.ok) throw new Error(`dumpsys failed: ${dumpsysRes.error}`);

      const appActivity = this.parseLauncherActivity(dumpsysRes.data.value || '', this.appPackage);
      if (!appActivity) throw new Error(`Could not parse launcher activity for ${this.appPackage}`);
      console.log('Detected activity:', appActivity);

      // ── Step 5: Launch via start_activity with am start fallback ────────────

      const startRes = await this.mCall<any>('startActivity', {}, {
        appPackage:      this.appPackage,
        appActivity:     appActivity,
        appWaitActivity: appActivity,
        appWaitDuration: 30000,
        intentAction:    'android.intent.action.MAIN',
        intentCategory:  'android.intent.category.LAUNCHER',
      });

      if (!startRes.ok) {
        console.warn('start_activity failed, falling back to am start');
        try {

          const amRes = await this.mCall<any>('executeSync', {}, {
            script: 'mobile: shell',
            args: [{
              command: 'am',
              args: [
                'start', '-n', `${this.appPackage}/${appActivity}`,
                '-a', 'android.intent.action.MAIN',
                '-c', 'android.intent.category.LAUNCHER'
              ]
            }]
          });
          const amOutput = (amRes.data.value || amRes.error || '').toString();
          if (amRes.ok && !amOutput.includes('does not exist') && !amOutput.includes('Error type 3')) {
            console.log('am start succeeded');
          } else {
            console.warn('am start also failed, monkey will handle it');
          }
        } catch {
          console.warn('am start threw, monkey will handle it');
        }
      }

      console.log('Running monkey launcher...');

      const monkeyRes = await this.mCall<any>('executeSync', {}, {
        script: 'mobile: shell',
        args: [{ command: 'monkey', args: ['-p', this.appPackage, '-c', 'android.intent.category.LAUNCHER', '1'] }]
      });
      if (!monkeyRes.ok) {
        console.warn('monkey failed — app may already be running');
      }

      await new Promise(resolve => setTimeout(resolve, 3000));
      console.log('App launched successfully');

    } catch (err) {
      // Clean up the session so the device is freed automatically
      console.error('Session setup failed, deleting session:', err);
      await this.rest.call(this.appiumBaseUrl, 'deleteSession', { sessionId: this.sessionId! }).catch(() => {});
      this.sessionId = null;
      throw err; // re-throw so the UI shows the error
    }

    // console.log(`📱 Running on: ${this.isEmulator ? 'Emulator' : 'Real device'}`);
    // console.log(`✅ Session: ${this.sessionId}`);
    // console.log(`📱 Device: ${caps.deviceManufacturer} ${caps.deviceModel} | Android ${caps.platformVersion}`);
    // console.log(`📐 Final resolution: ${this.imageWidth}x${this.imageHeight}`);
    // console.log(`🎯 Active driver: ${this.activeDriver}`);
  }

  async deleteSession(uninstall = false): Promise<void> {
    if (!this.sessionId) return;
    if (uninstall && this.appPackage) {
      await this.uninstallApp(this.appPackage);
    }
    // const deleteUrl = `${this.appiumWdUrl}/session/${this.sessionId}`;
    // await this.rest.raw(deleteUrl, 'DELETE');
    await this.mCall('deleteSession');
    this.sessionId = null;
  }

  async uninstallApp(packageName: string): Promise<void> {
    if (!this.sessionId) return;
    const res = await this.mCall('uninstallApp', {}, { appId: packageName });
    if (res.ok) {
      console.log(`Uninstalled: ${packageName}`);
    } else {
      console.warn(`Uninstall failed (non-fatal): ${res.error}`);
    }
  }

  reset(): void {
    this.sessionId                = null;
    this.udid                     = null;
    this.appPackage               = null;
    this.imageWidth               = 1080;
    this.imageHeight              = 2280;
    this.deviceResolutionResolved = false;
    this.isEmulator               = false;
    this.activeDriver             = 'uiautomator2';
    this._pageSourceFormatLogged  = false;
    this.selectedDevice           = null;
    // NOTE: appType is NOT reset — preserve user's last selection
  }

  private parseLauncherActivity(dumpsys: string, pkg: string): string | null {
    const mainBlockMatch = dumpsys.match(
      /android\.intent\.action\.MAIN[\s\S]*?android\.intent\.category\.LAUNCHER([\s\S]*?)(?:\n\s{4}\w|\n\s{0,2}\w|$)/
    );
    const searchBlock = mainBlockMatch ? mainBlockMatch[0] : dumpsys;
    const activityRegex = new RegExp(`${pkg.replace(/\./g, '\\.')}\\/([\\w.]+)`, 'g');
    let match: RegExpExecArray | null;
    while ((match = activityRegex.exec(searchBlock)) !== null) {
      const raw = match[1];
      return raw.startsWith('.') ? `${pkg}${raw}` : raw;
    }
    return null;
  }

  // ── Resolution helpers ───────────────────────────────────────────────────────

  async fetchDeviceResolution(): Promise<void> {
    const rectRes = await this.mCall<{ value: { width: number; height: number } }>('windowRect');
    if (rectRes.ok && rectRes.data.value) {
      const { width: w, height: h } = rectRes.data.value;
      if (w > 0 && h > 0) {
        this.imageWidth = w; this.imageHeight = h;
        this.deviceResolutionResolved = true;
        console.log(`Resolution${w}x${h}`);
        return;
      }
    } else {
      console.warn(' /window/rect failed:', rectRes.error);
    }

    const wmRes = await this.mCall<{ value: string }>('wmSize', {}, {
      script: 'mobile: shell',
      args: [{ command: 'wm', args: ['size'] }]
    });
    if (wmRes.ok && wmRes.data.value) {
      const match = (wmRes.data.value as string).match(/Physical size:\s*(\d+)x(\d+)/);
      if (match) {
        this.imageWidth = parseInt(match[1]); this.imageHeight = parseInt(match[2]);
        this.deviceResolutionResolved = true;
        console.log(` Resolution from wm size: ${this.imageWidth}x${this.imageHeight}`);
        return;
      }
    } else {
      console.warn(' mobile: shell wm size failed:', wmRes.error);
    }

    console.warn('All resolution strategies failed — will infer from first stream frame');
  }

  // ── Page source ───────────────────────────────────────────────────────────────

  async fetchPageSource(): Promise<string> {
    if (!this.sessionId) throw new Error('No session');
    if (this.activeDriver === 'flutter-integration') {
      return this._fetchFlutterPageSource();
    }
    return this._fetchNativePageSource();
  }

  private async _fetchNativePageSource(): Promise<string> {
    const res = await this.mCall<{ value: string }>('pageSource');
    if (!res.ok || !res.data.value) throw new Error('fetchPageSource failed');

    if (!this._pageSourceFormatLogged) {
      this._pageSourceFormatLogged = true;
      try {
        const doc     = new DOMParser().parseFromString(res.data.value, 'text/xml');
        const firstEl = doc.querySelector('*');
        // console.log(`📄 Native source root: <${doc.documentElement.tagName}>`);
        // console.log(`📄 First element: <${firstEl.tagName}> | bounds=${!!firstEl.getAttribute('bounds')}`);
        // console.log(`📄 First 300 chars:`, res.data.value.slice(0, 300));
      } catch {}
    }
    return res.data.value;
  }

  private async _fetchFlutterPageSource(): Promise<string> {
    // const url = FLUTTER_SOURCE_URL(this.appiumBaseUrl, this.sessionId!);
    // const res = await this.rest.raw<{ value: string }>(url, 'GET');
    const res = await this.mCall<{ value: string }>('flutterSource');

    if (!this._pageSourceFormatLogged) {
      this._pageSourceFormatLogged = true;
      if (res.ok && res.data.value) {
        try {
          const doc     = new DOMParser().parseFromString(res.data.value, 'text/xml');
          const firstEl = doc.querySelector('*');
          // console.log(`📄 Flutter source root: <${doc.documentElement.tagName}>`);
          // console.log(`📄 First element: <${firstEl.tagName}> | rect=${!!firstEl.getAttribute('rect')}`);
          // console.log(`📄 First 300 chars:`, res.data.value.slice(0, 300));
        } catch {}
      } else {
        console.warn('flutter_page_source failed, falling back to native /source');
      }
    }

    if (res.ok && res.data.value) {
      return res.data.value;
    }
    console.warn('Falling back to UiAutomator2 /source for Flutter app');
    return this._fetchNativePageSource();
  }

  private _pageSourceFormatLogged = false;

  // ── Element parsing (pure DOM — no HTTP calls) ────────────────────────────────

  parseElementFromSource(xml: string, x: number, y: number): RecordedAction['element'] {
    if (this.activeDriver === 'flutter-integration') {
      const hasRect = xml.includes(' rect=');
      if (hasRect) {
        return this._parseFlutterIntegrationSource(xml, x, y);
      }
      return this._parseNativeSource(xml, x, y);
    }
    return this._parseNativeSource(xml, x, y);
  }

  isInputElement(el: RecordedAction['element']): boolean {
    if (!el) return false;

    if (el.className) {
      if (el.className.includes('EditText') ||
          el.className.includes('AutoCompleteTextView') ||
          el.className.includes('TextInputEditText') ||
          el.className.includes('TextInputLayout')) {
        // EditText with text but no hint = dropdown trigger, not a real input.
        // Real inputs have a hint (placeholder). Dropdowns show a selected value
        // as text but carry no hint attribute.
        const hint    = ((el as any).hint || '').trim();
        const hasHint = !!hint;
        const hasText = !!el.text.trim();
        if (hasText && !hasHint) return false;
        return true;
      }
      if (el.className === 'TextField'     ||
          el.className === 'TextFormField' ||
          el.className === 'CupertinoTextField') return true;
    }

    const desc  = (el.accessibilityId || '').toLowerCase().trim();
    const resId = (el.resourceId      || '').toLowerCase();
    const hint  = ((el as any).hint   || '').toLowerCase().trim();

    const INPUT_RES_SUFFIXES = [
      'input', 'field', 'text_field', 'textfield', 'edittext',
      'edit_text', 'textinput', 'text_input', 'search', 'query'
    ];
    // Only treat resource-id suffix match as input when a hint is also present.
    // Dropdown triggers often have resource-ids like "text_input" but no hint.
    if (INPUT_RES_SUFFIXES.some(s =>
      resId.endsWith(s) || resId.includes(`_${s}_`) || resId.includes(`/${s}`)
    )) {
      if (hint) return true;
      return false;
    }

    const INPUT_HINT_PHRASES = [
      'enter ', 'type ', 'search for', 'search here',
      'email address', 'phone number', 'your name', 'full name',
      'username', 'password', 'confirm password', 'new password',
      'write a ', 'add a ', 'describe '
    ];
    if (INPUT_HINT_PHRASES.some(h => desc.startsWith(h) || desc.includes(h))) return true;
    if (hint && INPUT_HINT_PHRASES.some(h => hint.startsWith(h) || hint.includes(h))) return true;

    return false;
  }

  // ── Native UiAutomator2 parser ────────────────────────────────────────────────

  private _parseNativeSource(xml: string, x: number, y: number): RecordedAction['element'] {
    try {
      const doc      = new DOMParser().parseFromString(xml, 'text/xml');
      const allNodes = Array.from(doc.querySelectorAll('*'));

      type Candidate = {
        node: Element; area: number; depth: number;
        clickable: boolean; hasText: boolean; isContainer: boolean;
        hasSemantics: boolean; isRnLeaf: boolean;
      };

      const CONTAINER_CLASSES = new Set([
        'android.widget.FrameLayout', 'android.widget.LinearLayout',
        'android.widget.RelativeLayout', 'android.widget.ScrollView',
        'android.widget.HorizontalScrollView', 'android.widget.ListView',
        'android.widget.GridView', 'androidx.recyclerview.widget.RecyclerView',
        'android.view.ViewGroup',
      ]);
      const SEEKBAR_CLASSES = new Set([
        'android.widget.SeekBar',
        'android.widget.RatingBar',
        'android.widget.ProgressBar',
      ]);

      const candidates: Candidate[] = [];

      for (let i = 0; i < allNodes.length; i++) {
        const node   = allNodes[i];
        const bounds = node.getAttribute('bounds');
        if (!bounds) continue;
        const m = bounds.match(/\[(\d+),(\d+)\]\[(\d+),(\d+)\]/);
        if (!m) continue;
        const [, x1s, y1s, x2s, y2s] = m;
        const x1 = +x1s, y1 = +y1s, x2 = +x2s, y2 = +y2s;
        if (x < x1 || x > x2 || y < y1 || y > y2) continue;
        const area = (x2 - x1) * (y2 - y1);
        if (area <= 0) continue;

        const className   = node.getAttribute('class') || '';
        const clickable   = node.getAttribute('clickable') === 'true' || node.getAttribute('focusable') === 'true';
        const text        = node.getAttribute('text')         || '';
        const contentDesc = node.getAttribute('content-desc') || '';
        const resourceId  = node.getAttribute('resource-id')  || '';
        const hint        = node.getAttribute('hint')         || '';

        const hasText      = !!(text || contentDesc);
        const hasSemantics = !!(contentDesc || resourceId || hint);
        const isContainer  = CONTAINER_CLASSES.has(className);
        // RN renders interactive elements as plain android.view.View with clickable=true
        const isRnLeaf     = className === 'android.view.View' && clickable;

        candidates.push({ node, area, depth: i, clickable, hasText, isContainer, hasSemantics, isRnLeaf });
      }

      if (candidates.length === 0) return null;

      // ── Sort: original proven order, with container penalty added ─────────────
      candidates.sort((a, b) => {
        const aFullScreen = a.area > (this.imageWidth * this.imageHeight * 0.85);
        const bFullScreen = b.area > (this.imageWidth * this.imageHeight * 0.85);
        if (aFullScreen !== bFullScreen) return aFullScreen ? 1 : -1;
        const aIsSeekBar = SEEKBAR_CLASSES.has(a.node.getAttribute('class') || '');
        const bIsSeekBar = SEEKBAR_CLASSES.has(b.node.getAttribute('class') || '');
        if (aIsSeekBar !== bIsSeekBar) return aIsSeekBar ? -1 : 1;
        if (a.isContainer  !== b.isContainer)  return a.isContainer  ?  1 : -1;
        if (a.clickable    !== b.clickable)    return a.clickable    ? -1 : 1;
        if (a.hasSemantics !== b.hasSemantics) return a.hasSemantics ? -1 : 1;
        if (a.isRnLeaf     !== b.isRnLeaf)     return a.isRnLeaf     ? -1 : 1;
        if (a.hasText      !== b.hasText)      return a.hasText      ? -1 : 1;
        if (a.depth        !== b.depth)        return b.depth - a.depth;
        return a.area - b.area;
      });

      let best = candidates[0].node;

      const LIST_CONTAINER_CLASSES = new Set([
        'androidx.recyclerview.widget.RecyclerView',
        'android.widget.ListView',
        'android.widget.GridView',
        'android.widget.ExpandableListView',
        'androidx.viewpager.widget.ViewPager',
        'androidx.viewpager2.widget.ViewPager2',
        'android.widget.GridLayout',
        'com.google.android.material.tabs.TabLayout',
        'android.widget.TabHost',
        'android.widget.TabWidget',
        // 'android.widget.ScrollView',
        'android.widget.HorizontalScrollView',
        'androidx.core.widget.NestedScrollView',
        'com.google.android.material.chip.ChipGroup',
        'android.widget.RadioGroup',
        'com.google.android.material.bottomnavigation.BottomNavigationView',
        'com.google.android.material.navigationrail.NavigationRailView',
        // 'androidx.drawerlayout.widget.DrawerLayout',
        'android.widget.Spinner',
        'android.widget.TableLayout',
        // 'android.widget.TableRow',
      ]);

      // ── Post-sort fix 1: if best is a RecyclerView/ListView, drill into it ───
      if (LIST_CONTAINER_CLASSES.has(best.getAttribute('class') || '')) {
        let tightest: Element | null = null;
        let tightestArea = Infinity;
        for (const node of allNodes) {
          if (!best.contains(node) || node === best) continue;
          const b2 = node.getAttribute('bounds');
          if (!b2) continue;
          const m2 = b2.match(/\[(\d+),(\d+)\]\[(\d+),(\d+)\]/);
          if (!m2) continue;
          const x1 = +m2[1], y1 = +m2[2], x2 = +m2[3], y2 = +m2[4];
          if (x < x1 || x > x2 || y < y1 || y > y2) continue;
          const area2 = (x2 - x1) * (y2 - y1);
          if (area2 <= 0 || area2 >= tightestArea) continue;
          tightest = node; tightestArea = area2;
        }
        if (tightest) best = tightest;
      }

      // ── Post-sort fix 2: if best is a ViewGroup container, check if a child  ─
      // has stronger semantics (resource-id or content-desc) at the tap point.
      // This restores picking ImageView[@content-desc] over its ViewGroup parent.
      const bestClass = best.getAttribute('class') || '';
      const bestIsGenericContainer =
        CONTAINER_CLASSES.has(bestClass) || bestClass === 'android.view.ViewGroup';

      if (bestIsGenericContainer) {
        let betterChild: Element | null = null;
        let betterArea = Infinity;
        for (const node of allNodes) {
          if (!best.contains(node) || node === best) continue;
          const resId = node.getAttribute('resource-id');
          const desc  = node.getAttribute('content-desc');
          if (!resId && !desc) continue;
          const b2 = node.getAttribute('bounds');
          if (!b2) continue;
          const m2 = b2.match(/\[(\d+),(\d+)\]\[(\d+),(\d+)\]/);
          if (!m2) continue;
          const x1 = +m2[1], y1 = +m2[2], x2 = +m2[3], y2 = +m2[4];
          if (x < x1 || x > x2 || y < y1 || y > y2) continue;
          const area2 = (x2 - x1) * (y2 - y1);
          if (area2 <= 0 || area2 >= betterArea) continue;
          betterChild = node; betterArea = area2;
        }
        if (betterChild) best = betterChild;
      }

      const getDepth = (node: Element): number => {
        let d = 0; let cur: Element | null = node;
        while (cur) { cur = cur.parentElement; d++; } return d;
      };

      const getAncestorAtDepth = (node: Element, depth: number): Element | null => {
        let cur: Element | null = node; let d = 0;
        while (cur) { if (d === depth) return cur; cur = cur.parentElement; d++; }
        return null;
      };

      const findDivergence = (nodeA: Element, nodeB: Element): number => {
        const dA = getDepth(nodeA); const dB = getDepth(nodeB);
        const minD = Math.min(dA, dB);
        for (let d = 1; d <= minD; d++) {
          const ancA = getAncestorAtDepth(nodeA, dA - d);
          const ancB = getAncestorAtDepth(nodeB, dB - d);
          if (!ancA || !ancB || ancA === ancB) continue;
          const pA = ancA.parentElement; const pB = ancB.parentElement;
          if (pA && pB && pA === pB) {
            const sibs = Array.from(pA.children);
            return sibs.indexOf(ancB) - sibs.indexOf(ancA);
          }
          break;
        }
        return 0;
      };

      let topLayerBest: Element | null = null;
      let topLayerArea = Infinity;
      for (const node of allNodes) {
        if (node === best) continue;
        const b2 = node.getAttribute('bounds'); if (!b2) continue;
        const m2 = b2.match(/\[(\d+),(\d+)\]\[(\d+),(\d+)\]/); if (!m2) continue;
        const x1 = +m2[1], y1 = +m2[2], x2 = +m2[3], y2 = +m2[4];
        if (x < x1 || x > x2 || y < y1 || y > y2) continue;
        const area2 = (x2 - x1) * (y2 - y1);
        if (area2 <= 0) continue;

        // NEW GUARD — only allow override if this node is actually meaningful,
        // not an empty/non-interactive decoy layer.
        const isMeaningful =
          node.getAttribute('clickable') === 'true' ||
          node.getAttribute('focusable') === 'true' ||
          !!node.getAttribute('resource-id') ||
          !!node.getAttribute('content-desc') ||
          !!node.getAttribute('text') ||
          node.children.length > 0;
        if (!isMeaningful) continue;

        if (findDivergence(best, node) > 0 && area2 < topLayerArea) {
          topLayerBest = node; topLayerArea = area2;
        }
      }
      if (topLayerBest) best = topLayerBest;

      return {
        xpaths:          this._generateNativeXPaths(best),
        accessibilityId: best.getAttribute('content-desc') || undefined,
        resourceId:      best.getAttribute('resource-id')  || undefined,
        className:       best.getAttribute('class')        || undefined,
        text:            best.getAttribute('text')         || undefined,
        hint:            best.getAttribute('hint')         || undefined,
        editable:        best.getAttribute('editable') !== 'false',
      };
    } catch { return null; }
  }

  // ── Flutter Integration driver parser ─────────────────────────────────────────

  private _parseFlutterIntegrationSource(xml: string, x: number, y: number): RecordedAction['element'] {
    try {
      const doc      = new DOMParser().parseFromString(xml, 'text/xml');
      const allNodes = Array.from(doc.querySelectorAll('*'));

      const INTERACTIVE_WIDGETS = new Set([
        'ElevatedButton', 'TextButton', 'OutlinedButton', 'FilledButton',
        'IconButton', 'FloatingActionButton', 'GestureDetector', 'InkWell',
        'InkResponse', 'CupertinoButton', 'PopupMenuButton', 'DropdownButton',
        'DropdownButtonFormField', 'Checkbox', 'CheckboxListTile',
        'Switch', 'SwitchListTile', 'Radio', 'RadioListTile',
        'Slider', 'RangeSlider', 'TextField', 'TextFormField',
        'CupertinoTextField', 'SearchBar', 'ListTile', 'CheckboxMenuButton',
        'MenuItemButton', 'SubmenuButton', 'Tab', 'BottomNavigationBarItem',
        'NavigationDestination', 'ExpansionTile', 'ExpansionPanelList',
        'Chip', 'ActionChip', 'FilterChip', 'ChoiceChip', 'InputChip',
        'SegmentedButton', 'ToggleButtons',
      ]);

      const CONTAINER_WIDGETS = new Set([
        'Container', 'SizedBox', 'Padding', 'Center', 'Align', 'Column',
        'Row', 'Stack', 'Scaffold', 'SafeArea', 'Expanded', 'Flexible',
        'DecoratedBox', 'ColoredBox', 'ClipRRect', 'ClipRect', 'ClipOval',
        'Hero', 'AnimatedContainer', 'AnimatedOpacity', 'FadeTransition',
        'ScaleTransition', 'CustomScrollView', 'ListView', 'GridView',
        'SingleChildScrollView', 'PageView', 'NestedScrollView',
        'CustomPaint', 'RepaintBoundary', 'AbsorbPointer', 'IgnorePointer',
        'Offstage', 'Visibility', 'Opacity', 'FractionalTranslation',
        'Transform', 'Baseline', 'IntrinsicHeight', 'IntrinsicWidth',
        'ConstrainedBox', 'UnconstrainedBox', 'OverflowBox',
        'FittedBox', 'AspectRatio', 'FractionallySizedBox', 'Wrap', 'Flow', 'Table',
      ]);

      type FlutterCandidate = {
        node: Element; area: number; depth: number; tag: string;
        isInteractive: boolean; isContainer: boolean;
        hasKey: boolean; hasLabel: boolean; hasText: boolean; enabled: boolean;
        rect: { left: number; top: number; right: number; bottom: number };
      };

      const candidates: FlutterCandidate[] = [];

      for (let i = 0; i < allNodes.length; i++) {
        const node     = allNodes[i];
        const rectAttr = node.getAttribute('rect');
        if (!rectAttr) continue;
        let rect: { left: number; top: number; right: number; bottom: number };
        try { rect = JSON.parse(rectAttr); } catch { continue; }
        const { left, top, right, bottom } = rect;
        if (x < left || x > right || y < top || y > bottom) continue;
        const area = (right - left) * (bottom - top);
        if (area <= 0) continue;
        const tag     = node.tagName;
        const key     = node.getAttribute('key')   || '';
        const label   = node.getAttribute('label') || '';
        const text    = node.getAttribute('text')  || '';
        const enabled = node.getAttribute('enabled') !== 'false';
        candidates.push({
          node, area, depth: i, tag, rect,
          isInteractive: INTERACTIVE_WIDGETS.has(tag),
          isContainer:   CONTAINER_WIDGETS.has(tag),
          hasKey: !!key, hasLabel: !!label, hasText: !!text, enabled,
        });
      }

      if (candidates.length === 0) return null;

      candidates.sort((a, b) => {
        if (a.isInteractive !== b.isInteractive) return a.isInteractive ? -1 : 1;
        if (a.isContainer   !== b.isContainer)   return a.isContainer   ?  1 : -1;
        if (a.hasKey        !== b.hasKey)         return a.hasKey        ? -1 : 1;
        if (a.hasLabel      !== b.hasLabel)       return a.hasLabel      ? -1 : 1;
        if (a.hasText       !== b.hasText)        return a.hasText       ? -1 : 1;
        if (a.enabled       !== b.enabled)        return a.enabled       ? -1 : 1;
        if (a.depth         !== b.depth)          return b.depth - a.depth;
        return a.area - b.area;
      });

      const best      = candidates[0].node;
      const tag       = best.tagName;
      const key       = best.getAttribute('key')   || undefined;
      const label     = best.getAttribute('label') || undefined;
      const hint      = best.getAttribute('hint')  || undefined;
      const text      = best.getAttribute('text')  || undefined;
      const valueAttr = best.getAttribute('value') || undefined;

      const absoluteXpath    = this._buildFlutterXPath(best);
      const textBasedXpath   = (tag && text)  ? `//${tag}[@text='${text}']`    : undefined;
      const resourceIdXpath  = (tag && key)   ? `//${tag}[@key='${key}']`      : undefined;
      const contentDescXpath = (tag && label) ? `//${tag}[@label='${label}']`  : undefined;
      const relativeXpath    = (() => {
        const parts: string[] = [];
        if (key)   parts.push(`@key='${key}'`);
        if (label) parts.push(`@label='${label}'`);
        if (text)  parts.push(`@text='${text}'`);
        return parts.length > 1 ? `//${tag}[${parts.join(' and ')}]` : undefined;
      })();

      return {
        xpaths: { absoluteXpath, textBasedXpath, resourceIdXpath, contentDescXpath, relativeXpath },
        accessibilityId: label || hint,
        resourceId:      key,
        className:       tag,
        text:            text || valueAttr,
      };
    } catch { return null; }
  }

  // ── XPath builders ────────────────────────────────────────────────────────────

  private _buildFlutterXPath(node: Element): string {
    const parts: string[] = [];
    let cur: Element | null = node;
    while (cur && cur.nodeType === Node.ELEMENT_NODE) {
      const tag    = cur.tagName;
      const parent = cur.parentElement;
      if (parent && parent.nodeType === Node.ELEMENT_NODE) {
        const siblings = Array.from(parent.children).filter(c => c.tagName === tag);
        const idx      = siblings.indexOf(cur) + 1;
        parts.unshift(siblings.length > 1 ? `${tag}[${idx}]` : tag);
      } else { parts.unshift(tag); break; }
      cur = parent;
    }
    return '//' + parts.join('/');
  }

  private _generateNativeXPaths(node: Element) {
    const className = node.getAttribute('class');
    const text      = node.getAttribute('text');
    const resId     = node.getAttribute('resource-id');
    const desc      = node.getAttribute('content-desc');
    const isFlutterSemanticsNode = className === 'android.view.View' && !!desc && !resId && !text;
    const absoluteXpath = isFlutterSemanticsNode ? undefined : this._buildNativeXPath(node);
    let textBasedXpath:   string | undefined;
    let resourceIdXpath:  string | undefined;
    let contentDescXpath: string | undefined;
    let relativeXpath:    string | undefined;

    const doc = node.ownerDocument;

    if (className && resId) {
      const sameResIdNodes = Array.from(doc.querySelectorAll(`[resource-id="${CSS.escape(resId)}"]`))
        .filter(n => n.getAttribute('class') === className);
      if (sameResIdNodes.length <= 1) {
        resourceIdXpath = `//${className}[@resource-id='${resId}']`;
      } else {
        const pos = sameResIdNodes.indexOf(node) + 1;
        resourceIdXpath = `(//${className}[@resource-id='${resId}'])[${pos}]`;
      }
    }

    // ── contentDescXpath — content-desc is usually unique, but guard anyway ───
    if (className && desc) {
      const sameDescNodes = Array.from(doc.querySelectorAll(`[content-desc="${CSS.escape(desc)}"]`))
        .filter(n => n.getAttribute('class') === className);
      if (sameDescNodes.length <= 1) {
        contentDescXpath = `//${className}[@content-desc='${desc}']`;
      } else {
        const pos = sameDescNodes.indexOf(node) + 1;
        contentDescXpath = `(//${className}[@content-desc='${desc}'])[${pos}]`;
      }
    }

    // ── textBasedXpath — add positional index when text is not unique ─────────
    if (className && text) {
      const sameTextNodes = Array.from(doc.querySelectorAll(`[text="${CSS.escape(text)}"]`))
        .filter(n => n.getAttribute('class') === className);
      if (sameTextNodes.length <= 1) {
        textBasedXpath = `//${className}[@text='${text}']`;
      } else {
        const pos = sameTextNodes.indexOf(node) + 1;
        textBasedXpath = `(//${className}[@text='${text}'])[${pos}]`;
      }
    }

    // ── relativeXpath — resource-id + text combo, indexed if needed ───────────
    if (className && resId && text) {
      const sameComboNodes = Array.from(doc.querySelectorAll(`[resource-id="${CSS.escape(resId)}"]`))
        .filter(n => n.getAttribute('class') === className && n.getAttribute('text') === text);
      if (sameComboNodes.length <= 1) {
        relativeXpath = `//${className}[@resource-id='${resId}' and @text='${text}']`;
      } else {
        const pos = sameComboNodes.indexOf(node) + 1;
        relativeXpath = `(//${className}[@resource-id='${resId}' and @text='${text}'])[${pos}]`;
      }
    }

    const hasOwnSemantics = !!(resId || desc || text);

    // ── Child text fallback — runs BEFORE ancestor walk ───────────────────────
    // For Compose/RN views with no own semantics but a TextView child with text.
    // Prevents ancestor walk from anchoring on full-screen containers like
    // nav_host_fragment and generating fragile index-based xpaths.
    if (!textBasedXpath && !hasOwnSemantics && className) {
      // Collect ALL descendant texts, not just direct children
      // This handles deeply nested Compose/RN structures
      const allDescendants = Array.from(node.querySelectorAll('*'));
      const allTexts = allDescendants
        .map(c => (c.getAttribute('text') || '').trim())
        .filter(t => t.length > 0);

      if (allTexts.length > 0) {
        // Score each text — higher is better
        const scoreText = (t: string): number => {
          let score = 0;

          // Strongly prefer plain readable text (letters/digits)
          const letterCount = (t.match(/[a-zA-Z]/g) || []).length;
          score += letterCount * 10;

          // Penalize emoji-only strings
          const emojiOnly = /^[\u{1F000}-\u{1FFFF}\u{2600}-\u{27FF}\uD800-\uDFFF\s]+$/u.test(t);
          if (emojiOnly) score -= 100;

          // Penalize strings that are just numbers or symbols like "+91", "123"
          const numberOrSymbolOnly = /^[+\-\d\s]+$/.test(t);
          if (numberOrSymbolOnly) score -= 50;

          // Penalize very short strings (likely icons or codes)
          if (t.length <= 2) score -= 30;

          // Prefer longer readable text
          score += t.length;

          return score;
        };

        const bestText = allTexts.reduce((a, b) =>
          scoreText(a) >= scoreText(b) ? a : b
        );

        // Only use if it scored positively — avoids picking emoji/symbol fallbacks
        if (scoreText(bestText) > 0) {
          textBasedXpath = `//*[@text='${bestText}']`;
          relativeXpath  = textBasedXpath;
        }
      }
    }

    // Existing strategy: walk up ancestors for a resource-id / content-desc anchor
    if (!hasOwnSemantics && className) {
      const pathSegments: string[] = [];
      let cur: Element | null = node;
      let ancestor: Element | null = node.parentElement;

      while (ancestor && ancestor.tagName !== 'hierarchy') {
        const curClass = cur!.getAttribute('class') || cur!.tagName;
        const siblings = Array.from(ancestor.children)
          .filter(c => (c.getAttribute('class') || c.tagName) === curClass);
        const idx      = siblings.indexOf(cur!) + 1;
        const segment  = siblings.length > 1 ? `${curClass}[${idx}]` : curClass;
        pathSegments.unshift(segment);

        const aResId = ancestor.getAttribute('resource-id');
        const aDesc  = ancestor.getAttribute('content-desc');

        if (aResId || aDesc) {
          const relativePath  = pathSegments.join('/');
          const candidateXp   = aResId
            ? `//*[@resource-id='${aResId}']/${relativePath}`
            : `//*[@content-desc='${aDesc}']/${relativePath}`;

          let matchCount = 0;
          try {
            const iter = doc.evaluate(
              candidateXp, doc, null,
              XPathResult.ORDERED_NODE_ITERATOR_TYPE, null
            );
            while (iter.iterateNext()) { matchCount++; if (matchCount > 1) break; }
          } catch { matchCount = 0; }

          if (matchCount === 1) {
            if (aResId) { resourceIdXpath = candidateXp; relativeXpath = candidateXp; }
            else        { contentDescXpath = candidateXp; if (!relativeXpath) relativeXpath = candidateXp; }
            break;
          }
        }

        cur      = ancestor;
        ancestor = ancestor.parentElement;
      }
    
    }

    // RN fallback: anchor on a nearby Text node that carries visible text.
    // Only runs when we still have no text-based locator.
    if (!textBasedXpath && !resourceIdXpath && !contentDescXpath && className) {
      const childWithText = Array.from(node.children)
        .find(c => { const t = c.getAttribute('text'); return t && t.trim() !== ''; });
      const siblingWithText = node.parentElement
        ? Array.from(node.parentElement.children)
            .find(c => { if (c === node) return false; const t = c.getAttribute('text'); return t && t.trim() !== ''; }) || null
        : null;
      const nearbyNode = childWithText || siblingWithText || null;
      const nearbyText = nearbyNode ? nearbyNode.getAttribute('text') : null;
      if (nearbyText) {
        if (childWithText) {
          textBasedXpath = `//*[@text='${nearbyText}']/..`;
        } else {
          const siblings = Array.from(node.parentElement!.children);
          const nodeIdx  = siblings.indexOf(node) + 1;
          textBasedXpath = `//*[@text='${nearbyText}']/../*[${nodeIdx}]`;
        }
        if (!relativeXpath) relativeXpath = textBasedXpath;
      }
    }

    return { absoluteXpath, textBasedXpath, resourceIdXpath, contentDescXpath, relativeXpath };
  }

  private _buildNativeXPath(node: Element): string {
    const parts: string[] = [];
    let cur: Element | null = node;
    while (cur && cur.tagName !== 'hierarchy') {
      const tag    = cur.tagName;
      const parent = cur.parentElement;
      if (parent) {
        const siblings   = Array.from(parent.children).filter(c => c.tagName === tag);
        const idx        = siblings.indexOf(cur) + 1;
        // For RN's generic View classes, always include the index so the path is explicit
        const forceIndex = tag === 'android.view.View' || tag === 'android.view.ViewGroup';
        parts.unshift((siblings.length > 1 || forceIndex) ? `${tag}[${idx}]` : tag);
      } else { parts.unshift(tag); }
      cur = parent;
    }
    return '//' + parts.join('/');
  }

  // ── Appium Actions ────────────────────────────────────────────────────────────

  sendAction(action: 'tap' | 'longpress', x: number, y: number): void {
    if (!this.sessionId) return;
    const duration = action === Literals.LONG_PRESS ? 1000 : 100;
    this.mCall('sendActions', {}, {
      actions: [{ type: 'pointer', id: 'finger1', parameters: { pointerType: 'touch' },
        actions: [
          { type: 'pointerMove', duration: 0, x, y },
          { type: 'pointerDown', button: 0 },
          { type: 'pause', duration },
          { type: 'pointerUp', button: 0 }
        ]
      }]
    }).catch(console.error);
  }

  sendSwipe(x1: number, y1: number, x2: number, y2: number): void {
    if (!this.sessionId) return;
    this._sendW3CPointer([{
      id: 'finger1',
      actions: [
        { type: 'pointerMove', duration: 0, x: x1, y: y1 },
        { type: 'pointerDown', button: 0 },
        { type: 'pointerMove', duration: 600, origin: 'viewport', x: x2, y: y2 },
        { type: 'pointerUp', button: 0 }
      ]
    }]);
  }

  sendDoubleTap(x: number, y: number): void {
    if (!this.sessionId) return;
    this._sendW3CPointer([{
      id: 'finger1',
      actions: [
        { type: 'pointerMove', duration: 0, x, y },
        { type: 'pointerDown', button: 0 }, { type: 'pause', duration: 80 },
        { type: 'pointerUp',   button: 0 }, { type: 'pause', duration: 80 },
        { type: 'pointerDown', button: 0 }, { type: 'pause', duration: 80 },
        { type: 'pointerUp',   button: 0 }
      ]
    }]);
  }

  sendPinch(cx: number, cy: number, spread: number, pinch: number): void {
    if (!this.sessionId) return;
    if (this.isEmulator) { this.sendPinchW3C(cx, cy, spread, pinch); }
    else                 { this.sendPinchNative(cx, cy, spread, pinch); }
  }

  private sendPinchNative(cx: number, cy: number, spread: number, pinch: number): void {
    const script  = pinch === 0 ? 'mobile: pinchOpenGesture' : 'mobile: pinchCloseGesture';
    const boxSize = Math.round(spread * 2);
    this.mCall('wmSize', {}, {
      script, args: [{ left: Math.round(cx - spread), top: Math.round(cy - spread), width: boxSize, height: boxSize, percent: 0.5, speed: 2500 }]
    }).catch(console.error);
  }

  private sendPinchW3C(cx: number, cy: number, spread: number, pinch: number): void {
    const startSpread = pinch === 0 ? Math.round(spread * 0.5) : spread;
    const endSpread   = pinch === 0 ? spread : Math.round(spread * 0.25);
    this._sendW3CPointer([
      { id: 'finger1', actions: [
        { type: 'pointerMove', duration: 0,   origin: 'viewport', x: cx - startSpread, y: cy },
        { type: 'pointerDown', button: 0 },
        { type: 'pointerMove', duration: 600, origin: 'viewport', x: cx - endSpread,   y: cy },
        { type: 'pointerUp',   button: 0 }
      ]},
      { id: 'finger2', actions: [
        { type: 'pointerMove', duration: 0,   origin: 'viewport', x: cx + startSpread, y: cy },
        { type: 'pointerDown', button: 0 },
        { type: 'pointerMove', duration: 600, origin: 'viewport', x: cx + endSpread,   y: cy },
        { type: 'pointerUp',   button: 0 }
      ]}
    ], true);
  }

  sendTwoFingerSwipe(x1: number, y1: number, x2: number, y2: number): void {
    if (!this.sessionId) return;
    const offsetX = 60;
    this._sendW3CPointer([
      { id: 'finger1', actions: [
        { type: 'pointerMove', duration: 0, x: x1 - offsetX, y: y1 },
        { type: 'pointerDown', button: 0 },
        { type: 'pointerMove', duration: 500, x: x2 - offsetX, y: y2 },
        { type: 'pointerUp', button: 0 }
      ]},
      { id: 'finger2', actions: [
        { type: 'pointerMove', duration: 0, x: x1 + offsetX, y: y1 },
        { type: 'pointerDown', button: 0 },
        { type: 'pointerMove', duration: 500, x: x2 + offsetX, y: y2 },
        { type: 'pointerUp', button: 0 }
      ]}
    ], true);
  }

  sendThreeFingerSwipe(x1: number, y1: number, x2: number, y2: number): void {
    if (!this.sessionId) return;
    const offset = 80;
    this._sendW3CPointer([
      { id: 'finger1', actions: [
        { type: 'pointerMove', duration: 0, x: x1 - offset, y: y1 },
        { type: 'pointerDown', button: 0 },
        { type: 'pointerMove', duration: 500, x: x2 - offset, y: y2 },
        { type: 'pointerUp', button: 0 }
      ]},
      { id: 'finger2', actions: [
        { type: 'pointerMove', duration: 0, x: x1, y: y1 },
        { type: 'pointerDown', button: 0 },
        { type: 'pointerMove', duration: 500, x: x2, y: y2 },
        { type: 'pointerUp', button: 0 }
      ]},
      { id: 'finger3', actions: [
        { type: 'pointerMove', duration: 0, x: x1 + offset, y: y1 },
        { type: 'pointerDown', button: 0 },
        { type: 'pointerMove', duration: 500, x: x2 + offset, y: y2 },
        { type: 'pointerUp', button: 0 }
      ]}
    ], true);
  }

  sendType(text: string): void {
    if (!this.sessionId) return;
    const keyActions: { type: string; value: string }[] = [];
    text.split('').forEach(char => {
      keyActions.push({ type: 'keyDown', value: char });
      keyActions.push({ type: 'keyUp',   value: char });
    });
    this.mCall('sendActions', {}, {
      actions: [{ type: 'key', id: 'keyboard', actions: keyActions }]
    }).catch(console.error);
  }

  sendClearAndType(text: string): void {
    if (!this.sessionId) return;
    const keyActions: { type: string; value: string }[] = [];

    // Step 1: Ctrl+A — select all existing text
    keyActions.push({ type: 'keyDown', value: '\uE009' }); // Ctrl
    keyActions.push({ type: 'keyDown', value: 'a' });
    keyActions.push({ type: 'keyUp',   value: 'a' });
    keyActions.push({ type: 'keyUp',   value: '\uE009' }); // Ctrl

    // Step 2: Backspace — delete selected text
    keyActions.push({ type: 'keyDown', value: '\uE003' }); // Backspace
    keyActions.push({ type: 'keyUp',   value: '\uE003' });

    // Step 3: Type new text
    text.split('').forEach(char => {
      keyActions.push({ type: 'keyDown', value: char });
      keyActions.push({ type: 'keyUp',   value: char });
    });

    this.mCall('sendActions', {}, {
      actions: [{ type: 'key', id: 'keyboard', actions: keyActions }]
    }).catch(console.error);
  }

  pressKey(keycode: number): void {
    if (!this.sessionId) return;
    this.mCall('pressKeycode', {}, { keycode }).catch(console.error);
  }

  sendLongPress(x: number, y: number, holdMs: number): void {
    if (!this.sessionId) return;
    this._sendW3CPointer([{
      id: 'finger1',
      actions: [
        { type: 'pointerMove', duration: 0, x, y },
        { type: 'pointerDown', button: 0 },
        { type: 'pause',       duration: holdMs },
        { type: 'pointerUp',   button: 0 }
      ]
    }]);
  }

  private _sendW3CPointer(
    fingers: Array<{ id: string; actions: Record<string, unknown>[] }>,
    deleteAfter = false
  ): void {
    if (!this.sessionId) return;
    const body = {
      actions: fingers.map(f => ({
        type: 'pointer', id: f.id,
        parameters: { pointerType: 'touch' },
        actions: f.actions
      }))
    };
  
    const p = this.mCall('sendActions', {}, body);
    if (deleteAfter) {
      p.then(() => this.mCall('deleteActions')).catch(console.error);
    } else {
      p.catch(console.error);
    }
  }

  // ── WebSocket URL ─────────────────────────────────────────────────────────────

  getStreamUrl(): string {
    return this.rest.getStreamUrl(this.appiumBaseUrl, this.sessionId!);
  }

  private async installXapk(appUrl: string): Promise<void> {
    const xapkTmpPath = '/data/local/tmp/app_install.xapk';
    const extractDir  = '/data/local/tmp/xapk_extract';

    // ── Step A: Download XAPK bytes and push to device ────────────────────────
    console.log('Downloading XAPK for push...');
    const downloadRes = await fetch(appUrl);
    if (!downloadRes.ok) throw new Error(`Failed to download XAPK: ${downloadRes.status}`);

    const arrayBuffer = await downloadRes.arrayBuffer();
    const uint8Array  = new Uint8Array(arrayBuffer);

    // Convert to base64 in chunks to avoid call stack overflow
    let binary = '';
    const chunkSize = 8192;
    for (let i = 0; i < uint8Array.length; i += chunkSize) {
      binary += String.fromCharCode(...uint8Array.subarray(i, i + chunkSize));
    }
    const base64Data = btoa(binary);

    console.log('Pushing XAPK to device...');
    const pushRes = await this.mCall<any>('pushFile', {}, {
      path: xapkTmpPath,
      data: base64Data
    });
    if (!pushRes.ok) throw new Error(`push_file failed: ${pushRes.error}`);

    // ── Step B: Clean extract dir and unzip ──────────────────────────────────
    await this.mCall('executeSync', {}, {
      script: 'mobile: shell',
      args: [{ command: 'rm', args: ['-rf', extractDir] }]
    });
    await this.mCall('executeSync', {}, {
      script: 'mobile: shell',
      args: [{ command: 'mkdir', args: ['-p', extractDir] }]
    });
    const unzipRes = await this.mCall('executeSync', {}, {
      script: 'mobile: shell',
      args: [{ command: 'unzip', args: ['-o', xapkTmpPath, '*.apk', '-d', extractDir] }]
    });
    if (!unzipRes.ok) throw new Error(`unzip failed: ${unzipRes.error}`);
    console.log('XAPK extracted:', unzipRes.data.value);

    // ── Step C: List extracted APKs ───────────────────────────────────────────
    const lsRes = await this.mCall('executeSync', {}, {
      script: 'mobile: shell',
      args: [{ command: 'find', args: [extractDir, '-name', '*.apk'] }]
    });
    if (!lsRes.ok) throw new Error(`find APKs failed: ${lsRes.error}`);

    const apkPaths = (lsRes.data.value || '')
      .split('\n')
      .map((p: string) => p.trim())
      .filter(Boolean);

    if (apkPaths.length === 0) throw new Error('No APKs found in XAPK');
    console.log('APKs to install:', apkPaths);

    const installArgs = ['-r', '-t', ...apkPaths];
    const installRes  = await this.mCall('executeSync', {}, {
      script: 'mobile: shell',
      args: [{ command: 'pm', args: ['install', ...installArgs] }]
    });

    const installOutput = installRes.data.value || '';
    if (!installRes.ok || installOutput.includes('Failure') || installOutput.includes('Exception')) {
      throw new Error(`pm install failed: ${installOutput || installRes.error}`);
    }
    console.log('XAPK installed successfully:', installOutput);

    // ── Step G: Cleanup temp files ────────────────────────────────────────────
    await this.mCall('executeSync', {}, {
      script: 'mobile: shell',
      args: [{ command: 'rm', args: ['-rf', xapkTmpPath, extractDir] }]
    });
  }

  parseBoundsFromSource(
    xml: string, x: number, y: number
  ): { x1: number; y1: number; x2: number; y2: number } | null {
    try {
      const doc = new DOMParser().parseFromString(xml, 'text/xml');
      const el  = this.parseElementFromSource(xml, x, y);
      if (!el) return null;

      const allNodes = Array.from(doc.querySelectorAll('*'));

      // Strategy 1: match by resource-id + class (original — works for most elements)
      if (el.resourceId && el.className) {
        for (const node of allNodes) {
          if (
            node.getAttribute('resource-id') === el.resourceId &&
            node.getAttribute('class') === el.className
          ) {
            const raw = node.getAttribute('bounds');
            const m   = raw.match(/\[(\d+),(\d+)\]\[(\d+),(\d+)\]/);
            if (m) return { x1: +m[1], y1: +m[2], x2: +m[3], y2: +m[4] };
          }
        }
      }

      // Strategy 2: match by class + text (covers SeekBar with text="8923.0", etc.)
      if (el.className && el.text) {
        for (const node of allNodes) {
          if (
            node.getAttribute('class') === el.className &&
            node.getAttribute('text') === el.text
          ) {
            const raw = node.getAttribute('bounds');
            const m   = raw.match(/\[(\d+),(\d+)\]\[(\d+),(\d+)\]/);
            if (m) return { x1: +m[1], y1: +m[2], x2: +m[3], y2: +m[4] };
          }
        }
      }

      // Strategy 3: find smallest node containing (x, y) whose class matches
      if (el.className) {
        let best: { node: Element; area: number } | null = null;
        for (const node of allNodes) {
          if (node.getAttribute('class') !== el.className) continue;
          const raw = node.getAttribute('bounds');
          const m   = raw.match(/\[(\d+),(\d+)\]\[(\d+),(\d+)\]/);
          if (!m) continue;
          const x1 = +m[1], y1 = +m[2], x2 = +m[3], y2 = +m[4];
          if (x < x1 || x > x2 || y < y1 || y > y2) continue;
          const area = (x2 - x1) * (y2 - y1);
          if (!best || area < best.area) best = { node, area };
        }
        if (best) {
          const raw = best.node.getAttribute('bounds')!;
          const m   = raw.match(/\[(\d+),(\d+)\]\[(\d+),(\d+)\]/)!;
          return { x1: +m[1], y1: +m[2], x2: +m[3], y2: +m[4] };
        }
      }

      return null;
    } catch { return null; }
  }

}